import { Injectable, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { combineLatest, of, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { TcsConfigurationService } from 'src/app/core/configuration.service';
import { BreadcrumbService } from 'src/lib/amg-breadcrumbs/breadcrumb.service';
import { TabsService } from 'src/lib/tcs-tabs/tabs.service';
import { combineTitleSegments, getTitleSegments } from 'src/util/angular/ng-router-helpers';

@Injectable({
  providedIn: 'root',
})
export class RouteTitleService implements OnDestroy {
  private _onDestroy$ = new Subject();
  public enabled = false;

  constructor(
    private _titleService: Title,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _breadcrumbs: BreadcrumbService,
    private _config: TcsConfigurationService,
    private _tabs: TabsService,
  ) {}

  public setTitleOnNavigation() {
    if (!this.enabled) {
      this.enabled = true;
      this._router.events
        .pipe(
          filter((event: any) => event instanceof NavigationEnd),
          map(() => this._activatedRoute.snapshot),
          filter((r) => r.outlet === 'primary'),
          switchMap((r) =>
            this._breadcrumbs.value$.pipe(
              map((breadcrumbs) => {
                const titleSegments = getTitleSegments(r);
                let title: string | undefined;
                if (titleSegments.length > 0) {
                  title = combineTitleSegments(titleSegments);
                } else if (breadcrumbs.length > 0) {
                  title = breadcrumbs
                    .map((b) => b.name)
                    .reverse()
                    .join(' · ');
                }
                return title;
              }),
            ),
          ),
          switchMap((title) => combineLatest([of(title), this._tabs.isTabActive$])),
          takeUntil(this._onDestroy$),
        )
        .subscribe(([title, isTabActive]) => {
          const prefix = isTabActive ? '*' : '';
          const envSuffix =
            this._config.environmentKey === 'prod' ? '' : ` (${this._config.environmentDisplay})`;
          const titleStart = title != null ? `${title} | ` : '';
          this._titleService.setTitle(`${prefix}${titleStart}TCS${envSuffix}`);
        });
    }
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
